import { MutableRefObject, ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { SavedWord, savedWordFragmentId, useSavedWords } from "../../hooks/useSavedWords";
import UsuallyKanaWord from "../UsuallyKanaWord/UsuallyKanaWord";
import { useAuth } from "../../context/AuthContext";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { lookupSavedWordDetails } from "../../utils/savedWordLookup";
import styles from "./savedWords.module.scss";

export default function SavedWordsPage() {
    const { t, i18n } = useTranslation("savedWordsPage");
    const lp = useLocalizedPath();
    const location = useLocation();
    const isPt = i18n.language === "pt";
    const { status, user } = useAuth();
    const { words, remove, enrich, syncStatus } = useSavedWords();

    useEffect(() => {
        words.filter((word) => word.reading && (!word.displayWord || !word.romaji || !word.meaning)).forEach((word) => {
            void lookupSavedWordDetails(word.text, word.reading).then((details) => void enrich(word, details));
        });
    }, [words, enrich]);

    // Arriving from the saved-word popover's "View" link (#<fragment id>, see
    // savedWordFragmentId) scrolls straight to that one entry among however
    // many are saved, and briefly highlights it so it's obviously "the one" -
    // same scroll/focus/highlight pattern as LessonsPage's scrollToLesson.
    // Depends on words, status and syncStatus (not just hash) so this still
    // finds the <li> once the list itself has rendered: it only mounts after
    // auth and the device read have settled, same as the enrich effect above.
    const itemRefs = useRef(new Map<string, HTMLLIElement>());
    const [highlightedId, setHighlightedId] = useState<string | null>(null);
    const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.slice(1);
        const node = itemRefs.current.get(id);
        if (!node) return; // the word was removed elsewhere since the link was created - nothing to scroll/highlight
        const prefersReducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        node.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
        node.focus();
        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        setHighlightedId(id);
        highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedId(null);
            highlightTimeoutRef.current = null;
        }, 2200);
    }, [location.hash, words, status, syncStatus]);
    useEffect(
        () => () => {
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        },
        []
    );

    // Same signal the global "device progress" banner sends from its own
    // Retry button: useDeviceProgress listens for it and re-reads device storage.
    const retry = () => {
        window.dispatchEvent(new CustomEvent("nihongotech-device-retry", { detail: user?.identityId }));
    };

    // Auth resolves asynchronously ("loading" first), and the saved list then
    // loads from device storage, so neither a signed-out prompt nor "nothing
    // saved yet" may be shown until both have settled.
    let body: ReactNode;
    if (status === "loading" || (status === "authenticated" && syncStatus === "loading")) {
        body = <p className={styles.status} role="status">{t("loading")}</p>;
    } else if (status !== "authenticated") {
        body = (
            <>
                <p>{t("signedOut")}</p>
                <p><Link to={lp("/login")} state={{ from: location }} className={styles.signIn}>{t("signInLink")}</Link></p>
            </>
        );
    } else if (syncStatus === "error") {
        body = (
            <div className={styles.error} role="alert">
                <p>{t("loadError")}</p>
                <button type="button" onClick={retry}>{t("retry")}</button>
            </div>
        );
    } else if (words.length === 0) {
        body = (
            <>
                <p>{t("empty")}</p>
                <p className={styles.hint}>{t("emptyHint")}</p>
            </>
        );
    } else {
        body = (
            <ul className={styles.list} aria-label={t("listLabel")}>
                {words.map((word) => {
                    const id = savedWordFragmentId(word.text, word.reading);
                    return (
                        <SavedWordItem
                            key={`${word.text}:${word.reading}`}
                            word={word}
                            id={id}
                            highlighted={highlightedId === id}
                            itemRefs={itemRefs}
                            onRemove={() => void remove(word)}
                        />
                    );
                })}
            </ul>
        );
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={isPt ? "/pt/saved-words" : "/saved-words"}
                lang={isPt ? "pt-PT" : undefined}
                noindex
            />
            <Header />
            <main className={styles.main}>
                <h1>{t("title")}</h1>
                {body}
            </main>
            <Footer />
        </div>
    );
}

function SavedWordItem({
    word,
    id,
    highlighted,
    itemRefs,
    onRemove,
}: {
    word: SavedWord;
    id: string;
    highlighted: boolean;
    itemRefs: MutableRefObject<Map<string, HTMLLIElement>>;
    onRemove: () => void;
}) {
    const { t } = useTranslation("savedWordsPage");
    return (
        <li
            id={id}
            tabIndex={-1}
            ref={(node) => {
                if (node) itemRefs.current.set(id, node);
                else itemRefs.current.delete(id);
            }}
            className={highlighted ? styles.highlighted : undefined}
        >
            <strong><UsuallyKanaWord word={word.displayWord || word.text} alsoWritten={word.alsoWritten} /></strong>
            {word.reading && <span>{word.reading}</span>}
            {word.romaji && <span className={styles.romaji}>{word.romaji}</span>}
            {word.meaning && <p>{word.meaning}</p>}
            {word.baseWord && <p>{t("formOf", { word: word.baseWord })}</p>}
            {/* The visible text stays at the start of the accessible name (WCAG 2.5.3);
                the word makes each button distinguishable out of context. */}
            <button type="button" onClick={onRemove} aria-label={t("removeAria", { word: word.text })}>{t("remove")}</button>
        </li>
    );
}
